/**
 * Editorial provenance: the capability token that separates nodes this layer
 * generated from nodes that merely look like it.
 *
 * Trust is possession of the sentinel, never appearance. A `data-editorial-*`
 * attribute proves nothing on its own — untrusted markdown can author one.
 */
import type {
    EditorialGlyphUsage,
    EditorialSemantic,
} from "./editorial-policy";

export type EditorialProvenanceKind =
    | "glyph"
    | "structural-mark"
    /**
     * THIS transform emptied the container's display.
     *
     * Distinct from `structural-mark` on purpose. Both are annotations on an
     * authored node, so sharing one kind is tempting — but they answer
     * different questions, and one consumer asks each. Block elevation stamps
     * `structural-mark` to record "this block carries a role"; suppression asks
     * "did we empty this?" before deleting anything. While they shared a kind,
     * an elevated paragraph answered yes to a question nothing had asked it,
     * and the only thing keeping a glyph-only callout on screen was an
     * unrelated predicate.
     */
    | "emptied-container"
    | "tail-anchor";

export interface EditorialProvenance {
    readonly kind: EditorialProvenanceKind;
    readonly semantic?: EditorialSemantic;
    /**
     * Which presentation the glyph was minted for. Carried here rather than in
     * `properties` for the same reason the semantic is: a usage read off an
     * authorable attribute would let a message choose its own accessible label.
     *
     * Absent means `inline` — the only presentation the terminal transform
     * mints on its own. A block treatment re-marks the glyph it adopts.
     */
    readonly usage?: EditorialGlyphUsage;
    /**
     * Authored structural neighbours, recorded before the table-title lift can
     * remove one. Both directions are kept because the lift may consume the
     * neighbour on either side, and only a mark on the SURVIVING node stays
     * readable afterwards.
     */
    readonly authoredPrevious?: string;
    readonly authoredNext?: string;
}

// Module-private and never exported: holding this key IS the proof, so nothing
// outside this module can mint provenance. Defined non-enumerable so JSON,
// `Object.keys`, and object spread all drop it — serialized or cloned markup
// demotes to untrusted, which is the secure default rather than an oversight.
const EDITORIAL_PROVENANCE = Symbol("editorialProvenance");

interface ProvenanceCarrier {
    [EDITORIAL_PROVENANCE]?: EditorialProvenance;
}

const isCarrier = (value: unknown): value is ProvenanceCarrier =>
    value !== null && typeof value === "object";

/**
 * Mark a node this layer generated. Never touches the node's `properties`.
 *
 * Re-marking CARRIES FORWARD what the record says about the authored node, and
 * replaces everything the caller is entitled to decide. The split is the one
 * this file already draws between its fields, not a new policy:
 *
 * - `authoredPrevious` / `authoredNext` are facts about the SOURCE — what the
 *   message had on either side before any pass edited the tree. Only the
 *   structural pass can observe them, only once, and no later pass can
 *   reconstruct them. They survive.
 * - `kind`, `semantic` and `usage` describe what THIS mark makes the node.
 *   Every caller sets `kind`, and a caller that omits a semantic means the node
 *   has none — carrying a stale one forward would leave an elevated paragraph
 *   claiming to be a positive glyph.
 *
 * Merging here rather than at each call site: the wipe is invisible at the
 * caller (both writes look correct in isolation), silent in its effect, and the
 * next pass to re-mark anything would have to remember a rule nothing enforces.
 * The primitive is the only place that can see both records.
 */
export const markEditorial = <T>(
    node: T,
    provenance: EditorialProvenance
): T => {
    if (!isCarrier(node)) return node;

    const existing = node[EDITORIAL_PROVENANCE];
    // Spread LAST, so a pass that records adjacency explicitly still wins.
    const merged: EditorialProvenance = {
        ...(existing?.authoredPrevious !== undefined && {
            authoredPrevious: existing.authoredPrevious,
        }),
        ...(existing?.authoredNext !== undefined && {
            authoredNext: existing.authoredNext,
        }),
        ...provenance,
    };

    Object.defineProperty(node, EDITORIAL_PROVENANCE, {
        value: Object.freeze(merged),
        enumerable: false,
        writable: true,
        configurable: true,
    });
    return node;
};

/**
 * Fold an attribute name to the form a forgery cannot vary.
 *
 * hast normalizes `data-editorial-glyph` to `dataEditorialGlyph`, and HTML
 * attribute names are case-insensitive, so one attribute can arrive in several
 * spellings. Every reserved-name check compares on this form; string equality
 * lets a camelCased or hyphenated variant walk straight through.
 *
 * Shared rather than restated: this is the seam between authored markup and
 * the reserved namespace, and a second copy that missed a rule change would
 * leave one scrub — or the invariant harness that audits them — quietly blind.
 */
export const normalizeAttributeName = (name: string): string =>
    name.toLowerCase().replace(/[^a-z0-9]/g, "");

export const getEditorialProvenance = (
    node: unknown
): EditorialProvenance | undefined =>
    isCarrier(node) ? node[EDITORIAL_PROVENANCE] : undefined;

// Which kinds this layer MINTS. `structural-mark` is false deliberately: it
// records an authored fact onto a source node, so carrying it must not turn
// that node into editorial output.
//
// A record rather than a set, because the fallthrough is unsafe: an
// unclassified kind would read as source content, and both consumers then treat
// generated output as authored text — scrubbing its marks and typesetting it a
// second time. `satisfies` makes adding a kind without deciding a build error.
const KIND_IS_MINTED = {
    glyph: true,
    "structural-mark": false,
    // False, like `structural-mark`, and for the same reason: the container is
    // the author's and this only records what happened to it. Marking it minted
    // would tell the tail-anchor walk to step over an authored block.
    "emptied-container": false,
    "tail-anchor": true,
} satisfies Record<EditorialProvenanceKind, boolean>;

/**
 * True only for nodes this layer produced — not merely for nodes it annotated.
 * Callers deciding whether content is ours to own want this; callers asking
 * whether any provenance exists want `getEditorialProvenance`.
 */
export const isEditorialGenerated = (node: unknown): boolean => {
    const provenance = getEditorialProvenance(node);
    return provenance !== undefined && KIND_IS_MINTED[provenance.kind] === true;
};
